import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MetricsData {
  mrr: number;
  mrrChange: number;
  arr: number;
  arrChange: number;
  churn: number;
  churnChange: number;
  totalRevenue: number;
  revenueChange: number;
  cac: number;
  ltv: number;
  averageTicket: number;
  totalCustomers: number;
}

interface TimeSeriesData {
  month: string;
  revenue?: number;
  churn?: number;
  target?: number;
}

interface ProductReportData {
  product: {
    id: string;
    name: string;
  };
  metrics: MetricsData;
  timeSeries: {
    revenueData: TimeSeriesData[];
    churnData: TimeSeriesData[];
  };
}

interface ExcelExportData {
  metrics: MetricsData;
  revenueTimeSeries: TimeSeriesData[];
  churnTimeSeries: TimeSeriesData[];
  startDate: Date;
  endDate: Date;
  productName?: string;
  productDataList?: ProductReportData[];
}

const formatCurrency = (value: number) => {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercentage = (value: number) => {
  return `${value.toFixed(2)}%`;
};

export const exportMetricsToExcel = (data: ExcelExportData) => {
  try {
    const workbook = XLSX.utils.book_new();
    
    // Cenário 2: Se há apenas 1 produto, gerar só as abas desse produto
    if (data.productName && (!data.productDataList || data.productDataList.length === 0)) {
      const generalMetrics = [
        [`MÉTRICAS GERAIS - ${data.productName}`],
        ['Período', `${format(data.startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(data.endDate, 'dd/MM/yyyy', { locale: ptBR })}`],
        [],
        ['Métrica', 'Valor Atual', 'Variação'],
        ['MRR', formatCurrency(data.metrics.mrr), formatPercentage(data.metrics.mrrChange)],
        ['ARR', formatCurrency(data.metrics.arr), formatPercentage(data.metrics.arrChange)],
        ['Churn', formatPercentage(data.metrics.churn), formatPercentage(data.metrics.churnChange)],
        ['Receita Total', formatCurrency(data.metrics.totalRevenue), formatPercentage(data.metrics.revenueChange)],
        ['CAC', formatCurrency(data.metrics.cac), '-'],
        ['LTV', formatCurrency(data.metrics.ltv), '-'],
        ['Ticket Médio', formatCurrency(data.metrics.averageTicket), '-'],
        ['Total de Clientes', data.metrics.totalCustomers.toString(), '-'],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(generalMetrics);
      ws1['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }];
      
      const sheetName = data.productName.substring(0, 28);
      XLSX.utils.book_append_sheet(workbook, ws1, `${sheetName} - Geral`);

      if (data.revenueTimeSeries.length > 0) {
        const revenueData = [
          [`EVOLUÇÃO DE RECEITA - ${data.productName}`],
          [],
          ['Mês', 'Receita', 'Meta'],
          ...data.revenueTimeSeries.map(item => [
            item.month,
            item.revenue ? formatCurrency(item.revenue) : '-',
            item.target ? formatCurrency(item.target) : '-',
          ]),
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(revenueData);
        ws2['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(workbook, ws2, `${sheetName} - Receita`);
      }

      if (data.churnTimeSeries.length > 0) {
        const churnData = [
          [`EVOLUÇÃO DE CHURN - ${data.productName}`],
          [],
          ['Mês', 'Churn (%)', 'Meta (%)'],
          ...data.churnTimeSeries.map(item => [
            item.month,
            item.churn ? formatPercentage(item.churn) : '-',
            item.target ? formatPercentage(item.target) : '-',
          ]),
        ];

        const ws3 = XLSX.utils.aoa_to_sheet(churnData);
        ws3['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(workbook, ws3, `${sheetName} - Churn`);
      }
      
      const fileName = `dashi-metricas-${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      return true;
    }

    // Cenário 1: 2+ produtos - Gerar abas consolidadas + abas por produto
    // Aba 1: Métricas Gerais Consolidadas
    const consolidatedMetrics = [
      ['MÉTRICAS GERAIS - CONSOLIDADO (Todos os Produtos)'],
      ['Período', `${format(data.startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(data.endDate, 'dd/MM/yyyy', { locale: ptBR })}`],
      [],
      ['Métrica', 'Valor Atual', 'Variação'],
      ['MRR', formatCurrency(data.metrics.mrr), formatPercentage(data.metrics.mrrChange)],
      ['ARR', formatCurrency(data.metrics.arr), formatPercentage(data.metrics.arrChange)],
      ['Churn', formatPercentage(data.metrics.churn), formatPercentage(data.metrics.churnChange)],
      ['Receita Total', formatCurrency(data.metrics.totalRevenue), formatPercentage(data.metrics.revenueChange)],
      ['CAC', formatCurrency(data.metrics.cac), '-'],
      ['LTV', formatCurrency(data.metrics.ltv), '-'],
      ['Ticket Médio', formatCurrency(data.metrics.averageTicket), '-'],
      ['Total de Clientes', data.metrics.totalCustomers.toString(), '-'],
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(consolidatedMetrics);
    ws1['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, ws1, 'Consolidado - Geral');

    // Aba 2: Receita Consolidada
    if (data.revenueTimeSeries.length > 0) {
      const revenueData = [
        ['EVOLUÇÃO DE RECEITA - CONSOLIDADO'],
        [],
        ['Mês', 'Receita', 'Meta'],
        ...data.revenueTimeSeries.map(item => [
          item.month,
          item.revenue ? formatCurrency(item.revenue) : '-',
          item.target ? formatCurrency(item.target) : '-',
        ]),
      ];

      const ws2 = XLSX.utils.aoa_to_sheet(revenueData);
      ws2['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, ws2, 'Consolidado - Receita');
    }

    // Aba 3: Churn Consolidado
    if (data.churnTimeSeries.length > 0) {
      const churnData = [
        ['EVOLUÇÃO DE CHURN - CONSOLIDADO'],
        [],
        ['Mês', 'Churn (%)', 'Meta (%)'],
        ...data.churnTimeSeries.map(item => [
          item.month,
          item.churn ? formatPercentage(item.churn) : '-',
          item.target ? formatPercentage(item.target) : '-',
        ]),
      ];

      const ws3 = XLSX.utils.aoa_to_sheet(churnData);
      ws3['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, ws3, 'Consolidado - Churn');
    }

    // Abas por produto
    if (data.productDataList && data.productDataList.length > 0) {
      data.productDataList.forEach((productData) => {
        const productName = productData.product.name.substring(0, 28);
        
        // Aba de métricas gerais do produto
        const productMetrics = [
          [`MÉTRICAS GERAIS - ${productData.product.name}`],
          ['Período', `${format(data.startDate, 'dd/MM/yyyy', { locale: ptBR })} - ${format(data.endDate, 'dd/MM/yyyy', { locale: ptBR })}`],
          [],
          ['Métrica', 'Valor Atual', 'Variação'],
          ['MRR', formatCurrency(productData.metrics.mrr), formatPercentage(productData.metrics.mrrChange)],
          ['ARR', formatCurrency(productData.metrics.arr), formatPercentage(productData.metrics.arrChange)],
          ['Churn', formatPercentage(productData.metrics.churn), formatPercentage(productData.metrics.churnChange)],
          ['Receita Total', formatCurrency(productData.metrics.totalRevenue), formatPercentage(productData.metrics.revenueChange)],
          ['CAC', formatCurrency(productData.metrics.cac), '-'],
          ['LTV', formatCurrency(productData.metrics.ltv), '-'],
          ['Ticket Médio', formatCurrency(productData.metrics.averageTicket), '-'],
          ['Total de Clientes', productData.metrics.totalCustomers.toString(), '-'],
        ];

        const wsProduct = XLSX.utils.aoa_to_sheet(productMetrics);
        wsProduct['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(workbook, wsProduct, `${productName} - Geral`);

        // Aba de receita do produto
        if (productData.timeSeries.revenueData.length > 0) {
          const productRevenue = [
            [`EVOLUÇÃO DE RECEITA - ${productData.product.name}`],
            [],
            ['Mês', 'Receita', 'Meta'],
            ...productData.timeSeries.revenueData.map(item => [
              item.month,
              item.revenue ? formatCurrency(item.revenue) : '-',
              item.target ? formatCurrency(item.target) : '-',
            ]),
          ];

          const wsRevenue = XLSX.utils.aoa_to_sheet(productRevenue);
          wsRevenue['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }];
          XLSX.utils.book_append_sheet(workbook, wsRevenue, `${productName} - Receita`);
        }

        // Aba de churn do produto
        if (productData.timeSeries.churnData.length > 0) {
          const productChurn = [
            [`EVOLUÇÃO DE CHURN - ${productData.product.name}`],
            [],
            ['Mês', 'Churn (%)', 'Meta (%)'],
            ...productData.timeSeries.churnData.map(item => [
              item.month,
              item.churn ? formatPercentage(item.churn) : '-',
              item.target ? formatPercentage(item.target) : '-',
            ]),
          ];

          const wsChurn = XLSX.utils.aoa_to_sheet(productChurn);
          wsChurn['!cols'] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }];
          XLSX.utils.book_append_sheet(workbook, wsChurn, `${productName} - Churn`);
        }
      });
    }

    // Gerar arquivo
    const fileName = `dashi-metricas-${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return true;
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw new Error('Falha ao gerar arquivo Excel');
  }
};
